import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { listProducts, SLUG_TO_KEY } from '../lib/api';
import { formatMoney } from '../lib/pricing';

const CATEGORY_META = {
  'weight-loss': {
    key: 'weightLoss',
    title: 'Weight Loss Treatment Options',
    intro: 'Based on the information you provided, you may be eligible for one or more of the following treatments. Select the option you are most interested in. A licensed medical provider will review your information and determine the treatment and dosage that are medically appropriate for you.',
    subs: [
      { id: 'semaglutide', label: 'Semaglutide' },
      { id: 'tirzepatide', label: 'Tirzepatide' },
    ],
  },
};

export default function TreatmentSelection({ categorySlug }) {
  const meta = CATEGORY_META[categorySlug];
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prods = await listProducts(meta.key);
        if (!cancelled) setProducts(prods);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meta.key]);

  // Group by sub_category, honoring the meta order.
  const grouped = useMemo(() => {
    const active = products.filter((p) => p.status === 'active');
    const bySub = {};
    active.forEach((p) => {
      const s = p.sub_category || 'general';
      (bySub[s] = bySub[s] || []).push(p);
    });
    return meta.subs
      .map((s) => ({ ...s, items: bySub[s.id] || [] }))
      .filter((s) => s.items.length > 0);
  }, [products, meta.subs]);

  const firstName = (() => {
    try {
      const raw = localStorage.getItem(`alfa_quiz_${categorySlug}`);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.answers?.contact?.name?.split(' ')[0] || null;
    } catch { return null; }
  })();

  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="telegra-bg" aria-hidden="true" />
      <Header />

      <main className="flex-1 px-4 sm:px-6 pt-[96px] sm:pt-[128px] pb-16 sm:pb-24 max-w-[1200px] mx-auto w-full">
        <p data-testid="selection-kicker" className="uppercase text-[12.5px] tracking-[0.24em] text-neutral-950">
          Step 2 of 3 — Select your treatment
        </p>
        <h1 data-testid="selection-title" className="mt-3 font-display uppercase text-[36px] sm:text-[52px] md:text-[64px] leading-[0.98] tracking-[-0.015em] text-[color:var(--alfa-navy)]">
          {firstName ? <>Nice work, <span className="text-[color:var(--alfa-royal)]">{firstName}.</span><br /></> : null}
          {meta.title}
        </h1>
        <p className="mt-5 text-[16px] sm:text-[17px] text-neutral-950 leading-relaxed max-w-3xl">
          {meta.intro}
        </p>

        <div className="mt-4 inline-flex items-center gap-2 text-[13px] text-neutral-950 bg-white/70 border border-neutral-900/10 rounded-full px-3 py-1.5">
          <ShieldCheck size={14} className="text-emerald-600" />
          Prescription treatments require licensed provider approval before dispensing.
        </div>

        {loading && <p className="mt-10 text-neutral-950">Loading treatments…</p>}

        {!loading && grouped.map((section) => (
          <section key={section.id} className="mt-12">
            <h2 className="font-display uppercase text-[22px] sm:text-[26px] tracking-[-0.01em] text-[color:var(--alfa-navy)]">
              {section.label}
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {section.items.map((p) => (
                <TreatmentCard key={p.slug} p={p} onSelect={() => navigate(`/checkout/${p.slug}`)} />
              ))}
            </div>
          </section>
        ))}

        <div className="mt-14 flex flex-col sm:flex-row gap-3">
          <Link
            to={`/${categorySlug}/quiz`}
            className="inline-flex items-center justify-center gap-2 bg-white/70 hover:bg-white text-neutral-900 uppercase text-[13.5px] tracking-[0.14em] font-semibold px-5 py-3.5 rounded-sm border border-neutral-300"
          >
            Return to quiz
          </Link>
          <Link
            to={`/${categorySlug}`}
            className="inline-flex items-center justify-center gap-2 text-neutral-950 hover:text-neutral-950 uppercase text-[13.5px] tracking-[0.14em] font-semibold px-5 py-3.5"
          >
            Explore all {categorySlug.replace('-', ' ')}
          </Link>
        </div>

        {/* Membership Benefits */}
        <section className="mt-16 rounded-md border border-neutral-900/10 bg-white/85 backdrop-blur p-6 sm:p-8">
          <p className="uppercase text-[11.5px] tracking-[0.24em] text-neutral-950 font-semibold">Included with every plan</p>
          <h2 className="mt-3 font-display uppercase text-[24px] sm:text-[32px] leading-[1] tracking-[-0.01em] text-[color:var(--alfa-navy)]">
            The ALFÀ membership.
          </h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {[
              'Messaging with your care team',
              'Dosage adjustments as clinically appropriate',
              'Medication included, if prescribed',
              'Discreet shipping included',
              'Monthly refills, if prescribed',
              'HIPAA-secure health portal',
              'No insurance, no hidden fees',
              'Cancel any time',
            ].map((b) => (
              <div key={b} className="flex items-start gap-2 text-[14.5px] text-neutral-950">
                <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" strokeWidth={2.4} />
                <span>{b}</span>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function TreatmentCard({ p, onSelect }) {
  const monthly = p.monthly_price;
  return (
    <article
      data-testid={`treatment-card-${p.slug}`}
      className="rounded-md overflow-hidden bg-white/85 backdrop-blur border border-neutral-900/10 shadow-sm hover:shadow-lg transition-shadow flex flex-col"
    >
      <div className="relative aspect-[16/10] bg-neutral-100">
        {p.img ? (
          <img src={p.img} alt={p.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400 text-[13px] uppercase tracking-[0.2em]">
            {p.form || 'Rx'}
          </div>
        )}
        <span className="absolute top-3 left-3 bg-white text-[11.5px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-sm text-neutral-900">
          {p.tag}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display uppercase text-[19px] leading-tight text-[color:var(--alfa-navy)]">{p.name}</h3>
          <div className="text-right shrink-0">
            <span className="font-display text-[22px] text-neutral-950">{formatMoney(monthly)}</span>
            <span className="text-[13px] text-neutral-950">/mo</span>
          </div>
        </div>
        <p className="mt-1 text-[12.5px] uppercase tracking-[0.16em] text-neutral-950">{p.form}</p>
        <p className="mt-3 text-[14.5px] text-neutral-950 leading-relaxed">{p.desc}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(p.chips || []).slice(0, 3).map((c) => (
            <span key={c} className="text-[11.5px] border border-neutral-300 text-neutral-950 rounded-sm px-2 py-1">{c}</span>
          ))}
        </div>
        <div className="mt-auto pt-5">
          <button
            data-testid={`select-treatment-${p.slug}`}
            onClick={onSelect}
            className="btn-primary arrow-suffix w-full inline-flex items-center justify-center gap-2 uppercase text-[13.5px] tracking-[0.14em] font-semibold px-5 py-3 rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white"
          >
            Select this treatment <ArrowRight size={15} />
          </button>
          {p.provider_approval_required && (
            <p className="mt-3 text-[11.5px] text-neutral-950 flex items-center gap-1.5">
              <Check size={12} /> Requires provider approval
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
